// src/private/src/pages/app/clients/components/DrilldownPanel.jsx
import React, { memo, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { getMetricTier } from './MetricCell';
import DrilldownChart from './DrilldownChart';
import { computeBackendCounts, formatTerritory, BACKEND_PAGE_SIZE } from './dashboardUtils';

const TIER_CHIP_COLORS = {
  good: { bg: 'rgba(34, 197, 94, 0.12)', border: 'rgba(34, 197, 94, 0.35)', text: '#16a34a' },
  warn: { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.35)', text: '#d97706' },
  bad: { bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.35)', text: '#dc2626' },
  neutral: { bg: 'transparent', border: 'rgba(0,0,0,0.12)', text: 'text.secondary' },
};

const MetricChip = ({ label, value, format = 'number', metricKey }) => {
  const tier = metricKey ? getMetricTier(metricKey, value) : 'neutral';
  const colors = TIER_CHIP_COLORS[tier] || TIER_CHIP_COLORS.neutral;
  const display =
    format === 'dollar'
      ? `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
      : Number(value || 0).toLocaleString('en-US');

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: 0.4,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: colors.border,
        bgcolor: colors.bg,
      }}
    >
      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.text }}>
        {display}
      </Typography>
    </Box>
  );
};

/**
 * Unified drilldown panel used inside expanded table rows.
 * Replaces the inline rendering in MoM, DoD, Vertical, Channel, etc.
 *
 * Features:
 * - Funnel bar chart at top
 * - Metric chips instead of plain text counts
 * - Backend/Leads toggle
 * - Paginated tables
 * - Phone/Email hidden from non-staff users
 */
function DrilldownPanel({
  detail = {},
  title = '',
  startLabel,
  endLabel,
  onTabChange,
  onPageChange,
  isStaff = false,
  leadDetailBackPath = '',
  formatToEST,
  emptyLeadMessage = 'No lead records for this period.',
  emptyBackendMessage = 'No backend data for this period.',
}) {
  if (!detail?.expanded) return null;

  const counts = useMemo(() => computeBackendCounts(detail), [detail]);

  const backendRows = useMemo(() => {
    const rows = [...(detail.backendFull || detail.backend || [])];
    return rows.sort((a, b) => {
      const aTime = a?.sold_date ? new Date(a.sold_date).getTime() : 0;
      const bTime = b?.sold_date ? new Date(b.sold_date).getTime() : 0;
      return bTime - aTime;
    });
  }, [detail.backendFull, detail.backend]);

  const pagedBackend = useMemo(() => {
    const start = (detail.backendPage || 0) * BACKEND_PAGE_SIZE;
    return backendRows.slice(start, start + BACKEND_PAGE_SIZE);
  }, [backendRows, detail.backendPage]);

  const pagedLeads = useMemo(() => {
    const leads = detail.leads || [];
    const start = (detail.leadsPage || 0) * BACKEND_PAGE_SIZE;
    return leads.slice(start, start + BACKEND_PAGE_SIZE);
  }, [detail.leads, detail.leadsPage]);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="subtitle2" fontWeight={700}>
          {title} records
        </Typography>
        <ToggleButtonGroup
          size="small"
          color="primary"
          value={detail.activeTab || 'backend'}
          exclusive
          onChange={(_, val) => val && onTabChange(val)}
        >
          <ToggleButton value="backend">Backend</ToggleButton>
          <ToggleButton value="leads">Leads</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {(startLabel || endLabel) && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Range: {startLabel || '?'} – {endLabel || '?'}
        </Typography>
      )}

      {/* Funnel chart */}
      <DrilldownChart
        leads={detail.leads?.length || counts.bookedCount || 0}
        booked={counts.bookedCount}
        issues={counts.issueCount}
        demos={counts.demoCount}
        netSold={counts.netCount}
      />

      {/* Metric chips */}
      <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <MetricChip label="Booked" value={counts.bookedCount} />
        <MetricChip label="Scheduled" value={counts.apptCount} />
        <MetricChip label="Issues" value={counts.issueCount} />
        <MetricChip label="Demos" value={counts.demoCount} />
        <MetricChip label="Gross" value={counts.soldCount} />
        <MetricChip label="Net" value={counts.netCount} />
        <MetricChip label="Gross $" value={counts.grossSum} format="dollar" />
        <MetricChip label="Net $" value={counts.netSum} format="dollar" />
      </Stack>

      {/* Content */}
      {detail.loading ? (
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={16} width="60%" sx={{ borderRadius: 0.5 }} />
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
        </Stack>
      ) : detail.error ? (
        <Typography variant="body2" color="error">
          {detail.error}
        </Typography>
      ) : detail.activeTab === 'leads' ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Lead records ({detail.leadsCount || detail.leads?.length || 0})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, borderRadius: 1.5 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Date (EST)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Email</TableCell>
                                    {isStaff && <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Source</TableCell>}
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Territory</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Vertical</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>ZIP</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Self-Book</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Success</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedLeads.map((lead) => {
                  const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'View';
                  return (
                    <TableRow key={lead.id || name} sx={{ '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.015)' } }}>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{formatToEST?.(lead.date_created) || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {lead.id ? (
                          <Link
                            to={`/leads/${lead.id}`}
                            state={{ from: leadDetailBackPath }}
                            style={{ textDecoration: 'none', color: '#2f80ed' }}
                          >
                            {name}
                          </Link>
                        ) : name}
                      </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>{lead.phone || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{lead.email || '-'}</TableCell>
                                            {isStaff && <TableCell sx={{ fontSize: '0.75rem' }}>{lead.source || '-'}</TableCell>}
                      <TableCell sx={{ fontSize: '0.75rem' }}>{lead.territory || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{lead.vertical || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{lead.zip_code || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{lead.self_book_appointment_date_time || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{lead.success_post ? '✅' : '❌'}</TableCell>
                    </TableRow>
                  );
                })}
                {!pagedLeads.length && (
                  <TableRow>
                    <TableCell colSpan={isStaff ? 10 : 9} align="center" sx={{ py: 3 }}>
                      <Typography variant="caption" color="text.secondary">{emptyLeadMessage}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[BACKEND_PAGE_SIZE]}
            rowsPerPage={BACKEND_PAGE_SIZE}
            count={detail.leadsCount || detail.leads?.length || 0}
            page={detail.leadsPage || 0}
            onPageChange={(_, p) => onPageChange('leads', p)}
            sx={{ '& .MuiTablePagination-toolbar': { minHeight: 36 } }}
          />
        </Box>
      ) : (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Backend data ({backendRows.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, borderRadius: 1.5 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Sold Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Appt Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Issue</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Demo</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Gross</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Net</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Vertical</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>ZIP</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Territory</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedBackend.length > 0 ? pagedBackend.map((row) => (
                  <TableRow key={row.unique_id || row.id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.015)' } }}>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.sold_date || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.appointment_date || '-'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.phone || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.email || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.issue ? '✅' : '❌'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.demo ? '✅' : '❌'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.gross_amount ? `$${Number(row.gross_amount).toLocaleString()}` : '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.net_amount ? `$${Number(row.net_amount).toLocaleString()}` : '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.status || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.vertical || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {row.zip_code || row.client_zip_vertical_zip_code || row.client_zip_vertical?.zip_code || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{formatTerritory(row)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      <Typography variant="caption" color="text.secondary">{emptyBackendMessage}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[BACKEND_PAGE_SIZE]}
            rowsPerPage={BACKEND_PAGE_SIZE}
            count={detail.backendCount ?? backendRows.length}
            page={detail.backendPage || 0}
            onPageChange={(_, p) => onPageChange('backend', p)}
            sx={{ '& .MuiTablePagination-toolbar': { minHeight: 36 } }}
          />
        </Box>
      )}
    </Box>
  );
}

export default memo(DrilldownPanel);
